import { formatDicomName } from 'dicom';
import React, { useEffect, useState } from 'react';
import { Badge, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { BurnerJob } from 'reducers/printersReducer';
import { getBurnersJobs } from 'reducers/selectors';
import CustomDate from '../../../CustomDate';
import useToaster from 'actions/useToaster';
import getRimagePrioritiesOptions from '../RimageUtils';
import LinkedJobRow from './LinkedJobRow';
import JobActions from './JobsActions';
import { ellipseTooltipped, handleUpdatePriority, isPriorityChangeable, tooltipped } from './JobsTableUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

type JobsTableProps = {
  cancelJob: (jobId: string, burner: string) => void;
  filter: string;
};

const JobsTable = ({ cancelJob, filter }: JobsTableProps) => {
  const toaster = useToaster();
  const jobs: BurnerJob[] = useSelector(getBurnersJobs);
  const [filteredJobs, setFilteredJobs] = useState<BurnerJob[]>([]);
  const linkedJobsExpandedState: Map<string, boolean> = new Map();

  useEffect(() => {
    filterJobs();
  }, [JSON.stringify(jobs), filter]);

  const filterJobs = () => {
    const filtered: BurnerJob[] = jobs.filter(job => {
      if (job.burner && job.burner.trim() !== '') {
        const burnerName: string[] = job.burner.toLowerCase().split(' ');
        if (burnerName.some(name => name.startsWith(filter))) {
          return true;
        }
      }

      if (job.patientName && job.patientName.trim() !== '') {
        const patientName: string[] = formatDicomName(job.patientName.toLowerCase()).split(' ');
        if (patientName.some(name => name.startsWith(filter))) {
          return true;
        }
      }

      return job.studies.some(study => {
        if (study.description && study.description.trim() !== '') {
          const description = study.description.toLowerCase().split(' ');
          return description.some(descr => descr.startsWith(filter));
        }
        return false;
      });
    });
    setFilteredJobs(filtered);
  };

  const linkedJob = (filteredJob: BurnerJob): BurnerJob[] => {
    const id: string = filteredJob.id;
    let linkedJobs: BurnerJob[] = filteredJobs.filter(job => {
      return id === job.linkedJobId;
    });
    if (linkedJobs.length > 0) {
      return linkedJobs;
    }
    return [];
  };

  const filteredInstancesTooltip = (job: BurnerJob) => {
    return (
      <Tooltip id="tooltip">
        <p style={{ whiteSpace: 'pre-line', marginBottom: 0 }}>{job.filteredInstances + ' instances have been filtered'}</p>
      </Tooltip>
    );
  };

  return (
    <Row>
      <table className="queue-table" key={'jobs_table'}>
        <thead key={'jobs_table_thead'}>
          <tr>
            <th style={{ width: '1%' }}></th>
            <th style={{ width: '10%' }}>Job id</th>
            <th style={{ width: '19%' }}>Patient</th>
            <th style={{ width: '25%' }}>Description</th>
            <th style={{ width: '5%' }}>Series</th>
            <th style={{ width: '8%' }}>Date</th>
            <th style={{ width: '12%' }}>Status</th>
            <th style={{ width: '7%' }}>Error</th>
            <th style={{ width: '4%' }}>Priority</th>
            <th style={{ width: '4%' }}>
              <div style={{ display: 'flex', justifyContent: 'center' }}>Actions</div>
            </th>
          </tr>
        </thead>
        <tbody>
          {filteredJobs.map((job, index) => {
            if (job.linkedJobId !== undefined) {
              return undefined;
            }
            const support: BurnerJob[] = linkedJob(job);
            if (support.length + 1 > 1) {
              if (!linkedJobsExpandedState.has(job.id)) {
                linkedJobsExpandedState.set(job.id, false);
              }
              return (
                <LinkedJobRow
                  job={job}
                  index={index}
                  support={support}
                  cancelJob={cancelJob}
                  key={job.id + 'linkedJobRow'}
                />
              );
            } else {
              return (
                <tr key={job.id + '_' + index}>
                  <td />
                  <td>{ellipseTooltipped(job.id)}</td>
                  <td>{tooltipped(formatDicomName(job.patientName))}</td>
                  <td>
                    {job.studies.map(study => {
                      if (study.description?.trim() === '') {
                        return (
                          <div key={study.studyUid + '_container'}>
                            <i key={study.studyUid}>NO_DESCRIPTION</i>
                          </div>
                        );
                      }
                      return <div key={study.studyUid}>{tooltipped(study.description)}</div>;
                    })}
                  </td>
                  <td>
                    {job.studies.map(study => {
                      if (!study.series || study.series.length === 0) return <div key={study.studyUid}>ALL</div>;

                      return <div key={study.studyUid}>{'[' + study.series.length + '/' + study.totalSeries + ']'}</div>;
                    })}
                  </td>
                  <td>{CustomDate.fromString(job.date).toDateTimeString()}</td>
                  <td>
                    <div style={{ display: 'flex', flexDirection: 'row' }}>
                      {tooltipped(job.statusDescription, job.detailedStatusDescription)}

                      {job.filteredInstances > 0 && (
                        <div style={{ marginLeft: '0.3em' }}>
                          <OverlayTrigger placement="bottom" overlay={filteredInstancesTooltip(job)}>
                            <FontAwesomeIcon icon={faTriangleExclamation} className="warn-info-icon" />
                          </OverlayTrigger>
                        </div>
                      )}
                    </div>
                  </td>

                  <td>
                    <OverlayTrigger
                      placement="bottom"
                      delay={{ show: 250, hide: 400 }}
                      overlay={props => (
                        <Tooltip id="job-error-tooltip" {...props}>
                          <div style={{ fontSize: '16px' }} key={job.id + '_error'}>
                            {job.errorDescription}
                          </div>
                        </Tooltip>
                      )}
                    >
                      <Badge pill variant={job.statusDescription === 'Being processed' ? 'warning' : 'danger'}>
                        {job.errorCode}
                      </Badge>
                    </OverlayTrigger>
                  </td>
                  <td>
                    <div>
                      <Form.Control
                        as="select"
                        style={{
                          maxWidth: '5em',
                          minWidth: '5em',
                          paddingLeft: '2px',
                          paddingRight: '3px',
                          paddingTop: '0',
                          paddingBottom: '0',
                          marginLeft: '0',
                        }}
                        className="selectBoxNum selectFilter"
                        defaultValue={
                          job.burnerModel === 'EPSON_P100'
                            ? job.priority !== undefined
                              ? 'High'
                              : '-'
                            : job.burnerModel === 'RIMAGE_2000I'
                            ? job.priority + ''
                            : undefined
                        }
                        onChange={e => {
                          if (e.target.value !== job.priority) {
                            handleUpdatePriority(job, e.target.value, toaster);
                          }
                        }}
                        disabled={!isPriorityChangeable(job)}
                      >
                        {job.burnerModel === 'EPSON_P100' ? (
                          <React.Fragment>
                            <option value={'HIGH'}>High</option>
                            <option value={'-'}>-</option>
                          </React.Fragment>
                        ) : job.burnerModel === 'RIMAGE_2000I' ? (
                          getRimagePrioritiesOptions()
                        ) : (
                          <React.Fragment></React.Fragment>
                        )}
                      </Form.Control>
                    </div>
                  </td>
                  <td>
                    <Row style={{ justifyContent: 'center' }}>
                      <JobActions job={job} cancelJob={cancelJob} />
                    </Row>
                  </td>
                </tr>
              );
            }
          })}
        </tbody>
      </table>
    </Row>
  );
};

export default JobsTable;
